import { useContext, useMemo, useEffect, RefObject, useRef } from 'react';
import type { IControl, ControlPosition } from 'react-map-gl/dist/esm';
import { MapContext } from 'react-map-gl/dist/esm/components/map';
import type { MapContextValue } from 'react-map-gl/dist/esm/components/map';

type ControlOptions = {
    position?: ControlPosition;
};

export default function useControl<T extends IControl>(
    onCreate: (context: MapContextValue) => T,
    onRemove?: ((context: MapContextValue) => void) | ControlOptions,
    opts?: ControlOptions,
    containerNode?: RefObject<HTMLElement> | string,
) {
    const context = useContext(MapContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const ctrl = useMemo(() => onCreate(context), [onCreate, context]);
    const thisContainer = useRef<HTMLElement>();

    useEffect(() => {
        const { map } = context;

        if (!map.hasControl(ctrl)) {
            const node =
                typeof containerNode === 'string'
                    ? typeof document !== 'undefined' &&
                      document.getElementById(containerNode)
                    : containerNode?.current;

            if (!thisContainer.current && node) {
                thisContainer.current = node;

                // @ts-ignore
                thisContainer.current.appendChild(ctrl.onAdd(map));
            }
        }

        return () => {
            if (typeof onRemove === 'function') {
                onRemove(context);
            }
            // Map might have been removed (parent effects are destroyed before child ones)
            if (map.hasControl(ctrl)) {
                map.removeControl(ctrl);
            }
        };
    }, [containerNode, context, ctrl, onRemove, opts]);

    return ctrl;
}
